import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { OnboardedData, useComponentList } from '../api/getComponentList';
import {
  clearSelectedComponent,
  userSelectedComponent,
} from '@/state/slices/userSelectedComponentSlice';
import { registerTestIntelligenceScanSession } from '../api/registerScanSession';
import {
  type ComponentFilter,
  getComponentKind,
  matchesComponentSearch,
} from './componentInventoryUtils';
import ComponentsHero from './ComponentsHero';
import ComponentsControls, { type ComponentLayout } from './ComponentsControls';
import ComponentsEmptyState from './ComponentsEmptyState';
import ComponentsGridSkeleton from './ComponentsGridSkeleton';
import ComponentCard from './ComponentCard';
import ComponentTableView from './ComponentTableView';
import AddRepositoryDrawer from './AddRepositoryDrawer';
import {
  COMPONENTS_GRID,
  COMPONENTS_PAGE_BOTTOM,
  COMPONENTS_PAGE_INSET,
  COMPONENTS_PAGE_RAIL,
  COMPONENTS_PAGE_TOP,
} from './components-page-layout';
import { cn } from '@/lib/utils';

const DETECT_REPO_ROUTE = '/detect';

const ComponentListView = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<ComponentFilter>('all');
  const [layout, setLayout] = useState<ComponentLayout>('cards');
  const [onboardOpen, setOnboardOpen] = useState(false);
  const userSelectedComponentDispatch = useDispatch();
  const navigation = useNavigate();

  useEffect(() => {
    userSelectedComponentDispatch(clearSelectedComponent());
  }, [userSelectedComponentDispatch]);

  const handleUserSelectedComponents = (repo: OnboardedData) => {
    if (repo.isDeleted) return;
    userSelectedComponentDispatch(userSelectedComponent(repo));
    navigation(`code_analysis/inventory/${repo.dbRepoId}`);
    const scanId = repo.scanId?.trim();
    if (scanId) {
      void registerTestIntelligenceScanSession(scanId).catch(() => {
        /* non-blocking: graph still loads via call-graph API */
      });
    }
  };

  const handleDetectRepo = () => navigation(DETECT_REPO_ROUTE);
  const handleOnboard = () => setOnboardOpen(true);

  const { isPending, isError, data, error } = useComponentList();
  const components = data?.data ?? [];

  const filterCounts = useMemo(() => {
    const frontend = components.filter(
      item => getComponentKind(item) === 'frontend',
    ).length;
    const backend = components.filter(
      item => getComponentKind(item) === 'backend',
    ).length;

    return {
      all: components.length,
      frontend,
      backend,
    };
  }, [components]);

  const filteredComponents = useMemo(() => {
    const filtered = components.filter(item => {
      if (activeFilter !== 'all' && getComponentKind(item) !== activeFilter) {
        return false;
      }
      return matchesComponentSearch(item, searchQuery);
    });

    // Preserve LIFO onboarding order (newest first) through client filters
    return [...filtered].sort((a, b) => {
      const aTime = Date.parse(a.onboardedAt || '') || 0;
      const bTime = Date.parse(b.onboardedAt || '') || 0;
      if (bTime !== aTime) return bTime - aTime;
      return String(b.branchId || '').localeCompare(String(a.branchId || ''));
    });
  }, [activeFilter, components, searchQuery]);

  return (
    <div
      className={cn(
        COMPONENTS_PAGE_RAIL,
        COMPONENTS_PAGE_INSET,
        COMPONENTS_PAGE_TOP,
        COMPONENTS_PAGE_BOTTOM,
        'min-h-[calc(100vh-var(--workspace-header-height,2.75rem))]',
      )}>
      {/*
        Single vertical stack on a fixed rail.
        Hero, controls, and grid share the same left/right edges —
        the container defines width; cards only populate the grid.
      */}
      <div className="flex w-full flex-col gap-6">
        <ComponentsHero
          onDetectRepo={handleDetectRepo}
          onOnboard={handleOnboard}
        />

        <ComponentsControls
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          filterCounts={filterCounts}
          layout={layout}
          onLayoutChange={setLayout}
        />

        {isPending && <ComponentsGridSkeleton />}

        {isError && (
          <div
            className="rounded-[var(--inv-radius-control)] border border-solid border-[#fecdca] bg-[#fef3f2] px-4 py-3.5 text-sm text-[#b42318]"
            role="alert">
            Error: {error.message}
          </div>
        )}

        {!isPending && !isError && (
          <>
            {filteredComponents.length === 0 ? (
              <ComponentsEmptyState onOnboard={handleOnboard} />
            ) : layout === 'list' ? (
              <ComponentTableView
                data={filteredComponents}
                onSelectComponent={handleUserSelectedComponents}
              />
            ) : (
              <div className={COMPONENTS_GRID}>
                {filteredComponents.map(item => (
                  <ComponentCard
                    repo={item}
                    handleUserSelectedComponent={handleUserSelectedComponents}
                    key={`${encodeURIComponent(item?.scanId)}-${item.dbRepoId}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <AddRepositoryDrawer open={onboardOpen} onOpenChange={setOnboardOpen} />
    </div>
  );
};

export default ComponentListView;
